// Turns an API error into a message in the user's language.
// The backend answers with English `detail` texts; known texts are mapped to
// translation keys, anything unknown falls back to a translated generic message
// (English users still see the original backend text).
import i18n from '../i18n';

const KNOWN = [
  [/^Invalid credentials$/i, 'apiErrors.invalidCredentials'],
  [/^Email already registered$/i, 'apiErrors.emailRegistered'],
  [/^Account disabled/i, 'apiErrors.accountDisabled'],
  [/^Token expired$|^Invalid token$/i, 'apiErrors.sessionExpired'],
  [/^Group not found$/i, 'apiErrors.groupNotFound'],
  [/^Already a member of this group$/i, 'apiErrors.alreadyMember'],
  [/^Not a member of this group$/i, 'apiErrors.notMember'],
  [/^Topic not found$|^Invalid topic id$/i, 'apiErrors.topicNotFound'],
  [/^Question not found$|^Invalid question id$/i, 'apiErrors.questionNotFound'],
  [/^User not found$|^Invalid user id$/i, 'apiErrors.userNotFound'],
  [/^Quiz is locked after viewing answers$/i, 'apiErrors.quizLocked'],
  [/^Quiz index must be/i, 'apiErrors.quizNotFound'],
  [/^Maximum (\d+) attempts reached$/i, 'apiErrors.maxAttempts', (m) => ({ count: m[1] })],
  [/^No result found for this quiz$/i, 'apiErrors.noResult'],
  [/^No assessment data found$/i, 'apiErrors.noResult'],
  [/^Must submit exactly (\d+) answers$/i, 'apiErrors.answerAll', (m) => ({ count: m[1] })],
  [/^Complete at least 1 attempt to view answers$/i, 'apiErrors.attemptFirst'],
  [/^Complete all 10 quizzes to unlock bonus$/i, 'apiErrors.bonusLocked'],
  [/^Admin accounts cannot be deleted from the app$/i, 'apiErrors.adminDelete'],
  [/^Minimum payout amount is €([\d.]+)\. Your balance: €([\d.]+)$/i, 'apiErrors.minPayout', (m) => ({ min: m[1], balance: m[2] })],
  [/valid bank account|IBAN/i, 'apiErrors.invalidIban'],
  [/^Already reported$/i, 'apiErrors.alreadyReported'],
];

// Pydantic (422) validation errors: [{loc: ['body', 'email'], msg: '...'}]
function validationMessage(items, t) {
  const fields = items.map((e) => (Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : '')).filter(Boolean);
  if (fields.includes('email')) return t('apiErrors.invalidEmail');
  if (fields.includes('password')) return t('apiErrors.passwordShort');
  if (fields.includes('nickname')) return t('apiErrors.nicknameLength');
  if (fields.includes('code')) return t('apiErrors.groupNotFound');
  if (fields.includes('name')) return t('apiErrors.nameLength');
  return null;
}

/**
 * @param {any} error   axios error (or anything with response.data.detail)
 * @param {Function} t  i18next t function
 * @param {string} fallbackKey translation key used when the detail is unknown
 * @param {string} language current language (defaults to the app language)
 */
export function apiErrorMessage(error, t, fallbackKey = 'errors.generic', language = i18n.language) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return validationMessage(detail, t) || t(fallbackKey);
  }
  if (typeof detail === 'string' && detail.trim()) {
    for (const [re, key, params] of KNOWN) {
      const m = detail.match(re);
      if (m) return t(key, params ? params(m) : undefined);
    }
    if (String(language || 'en').startsWith('en')) return detail;
  }
  if (!error?.response) {
    // no answer from the server at all (offline, timeout)
    return t('apiErrors.network');
  }
  return t(fallbackKey);
}

export function isAlreadyMember(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && /already/i.test(detail);
}
